import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';

import RightSideDrawer from '../../components/RightSideDrawer/RightSideDrawer';
import CustomDatePicker from '../../components/Datepicker/Datepicker';
import Table from '../../components/Table/Table';
import { getAllWorkflows } from '../../services/workflows';
import { getAllJobs } from '../../services/jobs';
import { JOB_DETAILS_TABS, JobWorkflowStatus, JobWorkflowTaskStatus } from '../../utils/constants';
import { PageTitle } from '../../components/PageData/PageData';
import TabsSkeleton from '../../components/Loader/TabsSkeleton';
import StatusChip from '../../components/StatusChip/StatusChip';
import { isFileInput } from '../../utils/helpers';

import IconAI from '../../assets/icons/IconAI';
import IconView from '../../assets/icons/IconView';
import IconSearch from '../../assets/icons/IconSearch';
import IconChevDown from '../../assets/icons/IconChevDown';
import IconFilter from '../../assets/icons/IconFilter';
import IconCircleFile from '../../assets/icons/IconCircleFile';
import './Jobs.scss';
import { useDateByUserTimezone } from '../../hooks/useDateByUserTimeZone';

const Jobs = () => {
  const navigate = useNavigate();
  const { getDateByUserTimezone } = useDateByUserTimezone();

  const [isLoading, setIsLoading] = useState(true);
  const [isWorkflowLoading, setIsWorkflowLoading] = useState(true);
  const [tabValue, setTabValue] = useState();
  // const [globalFilter, setGlobalFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [date, setDate] = useState(null);

  const [searchValues, setSearchValues] = useState({
    ingestion: '',
    diligence: '',
  });
  const [workflows, setWorkflows] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const dummyRows = useMemo(
    () => Array.from({ length: 8 }).map((_, idx) => ({ id: `skeleton-${idx}` })),
    []
  );

  const fetchAllWorkflows = useCallback(async () => {
    setIsWorkflowLoading(true);
    const response = await getAllWorkflows({ params: { ordering: 'id', is_active: true } });
    if (response.success) {
      setWorkflows(response?.data?.results || []);
      setTabValue(response?.data?.results?.[0]?.id || 0);
      setIsWorkflowLoading(false);
    } else {
      setIsWorkflowLoading(false);
    }
  }, []);

  const fetchAllJobs = useCallback(
    async (params) => {
      setIsLoading(true);
      const page = params?.page ?? 1;
      const response = await getAllJobs({
        ...params,
        workflow: tabValue,
        page: page,
        page_size: params?.page_size ?? 10,
      });
      if (response.success) {
        const newExpanded = {};
        const jobsData =
          response?.data?.results &&
          response?.data?.results?.map((item, rowIndex) => {
            if (
              item?.rescheduled_jobs?.some(
                (sub) =>
                  sub.job_status === JobWorkflowTaskStatus.IN_PROGRESS.value ||
                  sub.job_status === JobWorkflowTaskStatus.INTERVENTION_NEEDED.value ||
                  sub.job_status === JobWorkflowTaskStatus.FAILED.value
              )
            ) {
              newExpanded[rowIndex.toString()] = true; // expand parent row
            }
            const formattedDate = item.created_date
              ? getDateByUserTimezone({
                  dateString: item.created_date,
                  shouldFormat: true,
                  dateFormat: 'MM-DD-YYYY HH:mm',
                })
              : '-';

            const child_data = item?.rescheduled_jobs?.map((subItem) => ({
              ...subItem,
              formatted_date: subItem.created_date
                ? getDateByUserTimezone({
                    dateString: subItem.created_date,
                    shouldFormat: true,
                    dateFormat: 'MM-DD-YYYY HH:mm',
                  })
                : '-',
            }));
            return { ...item, childData: child_data, formatted_date: formattedDate };
          });

        setTotalRecords(response.data.count);
        setExpandedRows(newExpanded);
        setJobs(jobsData || []);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabValue]
  );

  useEffect(() => {
    fetchAllWorkflows();
  }, [fetchAllWorkflows]);

  useEffect(() => {
    if (tabValue) fetchAllJobs();
    else if (!isWorkflowLoading && !tabValue) setIsLoading(false);
  }, [tabValue, fetchAllJobs, isWorkflowLoading]);

  const handleSearchChange = (value) => {
    setSearchValues((prev) => ({
      ...prev,
      [tabValue === 0 ? 'ingestion' : 'diligence']: value,
    }));
  };

  const handleSelectChange = (event) => setSelectedOption(event.target.value);

  const onApplyFilter = async () => {
    await fetchAllJobs({
      created_date: date && String(moment(date.toDate()).format('YYYY-MM-DD')),
      job_status: selectedOption,
      page: 1,
      page_size: pagination?.pageSize || 10,
    });
    setPagination({ pageIndex: 0, pageSize: pagination?.pageSize || 10 });
    setFilterDrawerOpen(false);
  };

  const drawerHead = <Typography variant='h6'>Filter By</Typography>;

  const drawerBody = () => (
    <>
      <Box mb={2}>
        <FormControl fullWidth>
          <Typography variant='formlabel' component='p' mb={1}>
            Date By
          </Typography>
          <CustomDatePicker value={date} onChange={(newValue) => setDate(newValue)} />
        </FormControl>
      </Box>
      <Box mb={2}>
        <FormControl fullWidth>
          <Typography variant='formlabel' component='p' mb={1}>
            Status
          </Typography>
          <Select value={selectedOption} onChange={handleSelectChange} displayEmpty>
            <MenuItem value='' disabled>
              Select Status
            </MenuItem>
            {Object.values(JobWorkflowStatus).map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </>
  );

  const drawerFoot = ({ onClose, onClear }) => (
    <Box display='flex' justifyContent='space-between' alignItems='center' width={'100%'}>
      {(date || selectedOption) && (
        <Button variant='text' size='small' color='secondary' onClick={onClear}>
          Clear All
        </Button>
      )}
      <Box
        display='flex'
        justifyContent='end'
        alignItems='center'
        width={date || selectedOption ? 'auto' : '100%'}
        gap={2}
      >
        <Button variant='outlined' size='small' color='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='contained' size='small' color='primary' onClick={onApplyFilter}>
          Apply
        </Button>
      </Box>
    </Box>
  );

  const onClear = () => {
    fetchAllJobs({ page: 1, page_size: pagination?.pageSize || 10 });
    setPagination({ pageIndex: 0, pageSize: pagination?.pageSize || 10 });
    setFilterDrawerOpen(false);
    setDate(null);
    setSelectedOption('');
  };

  const getSharedColumns = () => [
    {
      accessorKey: 'job_number',
      header: '#Activity ID',
      size: 140,
      enablePinning: true,
      pin: 'left',
      Cell: ({ cell }) => (
        <Link
          to={`/jobs/details/${cell?.row?.original?.id}`}
          component={RouterLink}
          underline='none'
          color='primary.main'
          fontWeight='600'
        >
          {cell.getValue()}
        </Link>
      ),
    },
    { accessorKey: 'merchant_name', header: 'Merchant', size: 140 },
    {
      accessorKey: 'formatted_date',
      header: 'Started at',
      size: 140,
      Cell: ({ cell }) => {
        const formatted_date = cell.getValue();
        return (
          <Box>
            <Typography variant='body2'>{formatted_date ?? '-'}</Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: 'exception_count',
      header: 'Exceptions',
      size: 140,
      Cell: ({ cell }) => {
        const exceptions = cell.getValue();
        return exceptions ?? '0';
      },
    },
    {
      accessorKey: 'current_stage',
      header: 'Current Stage',
      enableSorting: false,
      Cell: ({ cell }) => {
        const stage = cell.getValue();
        return (
          <Box>
            {!stage?.job_workflow_stage && !stage?.job_workflow_task_stage ? (
              <Typography variant='body2'>-</Typography>
            ) : (
              <>
                <Typography variant='body2' fontWeight={600} color='secondary.main'>
                  {stage?.job_workflow_stage ?? '-'}
                </Typography>
                <Typography
                  component={'div'}
                  display='flex'
                  alignItems='center'
                  color='text.secondary'
                  gap={0.5}
                >
                  <Box>
                    <IconAI />
                  </Box>
                  <Typography fontSize={13} lineHeight={1} mt={0.5}>
                    {stage?.job_workflow_task_stage ?? '-'}
                  </Typography>
                </Typography>
              </>
            )}
          </Box>
        );
      },
    },
    {
      accessorKey: 'job_status',
      header: 'Status',
      size: 140,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        const title =
          Object.values(JobWorkflowStatus).find((status) => status.value === value)?.label || value;
        return <StatusChip label={title} status={value} />;
      },
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 'auto',
      enableSorting: false,
      enablePinning: true,
      pin: 'right',
      muiTableHeadCellProps: {
        sx: {
          verticalAlign: 'middle',
          '& .Mui-TableHeadCell-Content': {
            justifyContent: 'end',
          },
        },
      },
      Cell: ({ cell }) => {
        let showViewIcon = true;
        let showDocIcon = true;
        if (
          !cell?.row?.original?.workflow_invoked &&
          cell?.row?.original?.job_status === JobWorkflowStatus.PENDING.value
        ) {
          showViewIcon = false;
          showDocIcon = false;
        } else if (
          cell?.row?.original?.job_failed_reason?.length &&
          cell?.row?.original?.job_status === JobWorkflowStatus.FAILED.value
        ) {
          showDocIcon = false;
        }
        return showViewIcon || showDocIcon ? (
          <Box display={'flex'} justifyContent={'end'}>
            {isFileInput(cell?.row?.original?.workflow_input_type) && showDocIcon && (
              <IconButton
                size='small'
                onClick={() =>
                  navigate(`/jobs/details/${cell?.row?.original?.id}`, {
                    state: { tabValue: JOB_DETAILS_TABS.DOCUMENTS },
                  })
                }
              >
                <IconCircleFile width={24} height={24} />
              </IconButton>
            )}
            {showViewIcon && (
              <IconButton
                size='small'
                onClick={() => navigate(`/jobs/details/${cell?.row?.original?.id}`)}
              >
                <IconView width={24} height={24} />
              </IconButton>
            )}
          </Box>
        ) : (
          <></>
        );
      },
    },
  ];

  const onPaginationChange = async (updater) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater;

    await fetchAllJobs({
      created_date: date && String(moment(date.toDate()).format('YYYY-MM-DD')),
      job_status: selectedOption,
      page: newPagination.pageIndex + 1,
      page_size: newPagination?.pageSize,
    });
    setPagination(newPagination);
  };

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <PageTitle breadcrumbs={[{ title: 'Activities', path: '/jobs', isActive: true }]} />
      <Box className='content-wrapper'>
        <Box className='content-scroll'>
          <Card sx={{ flex: 1 }} className='card-jobs'>
            <CardContent>
              <Grid container spacing={2} mb={2} alignItems={'center'}>
                <Grid size={{ xs: 12, md: 'auto' }} flex={{ xs: 1 }}>
                  {isWorkflowLoading ? (
                    <TabsSkeleton count={2} />
                  ) : (
                    <Tabs
                      value={tabValue}
                      onChange={(e, v) => setTabValue(v)}
                      variant='scrollable'
                      scrollButtons='false'
                    >
                      {workflows?.map((workflow, index) => (
                        <Tab label={workflow?.name} value={workflow?.id} key={index} />
                      ))}
                    </Tabs>
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 'auto' }}>
                  <Box
                    display={'flex'}
                    justifyContent={'flex-end'}
                    alignItems={'center'}
                    flexWrap={'wrap'}
                    gap={1.5}
                  >
                    <Box flex={1}>
                      <FormControl fullWidth>
                        <TextField
                          value={tabValue === 0 ? searchValues.ingestion : searchValues.diligence}
                          onChange={(e) => handleSearchChange(e.target.value)}
                          placeholder='Search here...'
                          variant='outlined'
                          size='small'
                          slotProps={{
                            input: {
                              startAdornment: <IconSearch sx={{ color: 'gray', mr: 1 }} />,
                            },
                          }}
                        />
                      </FormControl>
                    </Box>

                    <Badge
                      badgeContent={date || selectedOption ? ' ' : null}
                      variant={date || selectedOption ? 'dot' : null}
                      color='primary'
                    >
                      <Button
                        variant='outlined'
                        color='secondary'
                        sx={{ width: 40, height: 40, padding: 1 }}
                        onClick={() => setFilterDrawerOpen(true)}
                      >
                        <IconFilter width={18} height={18} />
                      </Button>
                    </Badge>
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={handleClick}
                      endIcon={<IconChevDown color='white' width={18} height={18} />}
                    >
                      Create Activity
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      {workflows?.map((workflow, index) => (
                        <MenuItem
                          key={index}
                          onClick={() => {
                            handleClose();
                            navigate('/jobs/create/', { state: { type: workflow } });
                          }}
                        >
                          {workflow?.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                </Grid>
              </Grid>

              <Table
                key={`${tabValue}-${jobs.length}-${jobs.some(
                  (row) => Array.isArray(row.childData) && row.childData.length > 0
                )}`}
                columns={getSharedColumns()}
                data={isLoading ? dummyRows : jobs}
                globalFilter={tabValue === 0 ? searchValues.ingestion : searchValues.diligence}
                onGlobalFilterChange={handleSearchChange}
                pinnedLeftColumns={['job_number']}
                expandedRows={expandedRows}
                onExpandedChange={setExpandedRows}
                enableSearch={true}
                enableFilters={true}
                showGlobalFilter={true}
                enablePagination={true}
                showBottomToolbar={true}
                showRowsPerPage={true}
                showPaginationArrows={true}
                manualPagination={true}
                rowCount={totalRecords}
                pagination={pagination}
                isLoading={isLoading}
                onPaginationChange={onPaginationChange}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>

      <RightSideDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        headerContent={drawerHead}
        bodyContent={drawerBody}
        footerContent={(onClose) => drawerFoot({ onClose, onClear })}
      />
    </>
  );
};

export default Jobs;
